/**
 * Audio Notifications Utility
 * Sistema di notifiche audio multi-tono per ambienti rumorosi (officina)
 *
 * Usa Web Audio API per generare sequenze di toni a frequenze diverse
 * che sono più facilmente percepibili rispetto a un singolo beep monotono.
 */

let audioContext: AudioContext | null = null;

/**
 * Inizializza AudioContext (richiede interazione utente su mobile)
 */
export function initAudioContext(): AudioContext | null {
  if (audioContext && audioContext.state !== 'closed') {
    return audioContext;
  }

  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    console.log('[Audio] ✅ AudioContext inizializzato');
    return audioContext;
  } catch (err) {
    console.error('[Audio] ❌ Errore inizializzazione AudioContext:', err);
    return null;
  }
}

/**
 * Resume AudioContext se in stato suspended (richiesto dopo interazione utente)
 */
export async function resumeAudioContext(): Promise<boolean> {
  const ctx = initAudioContext();
  if (!ctx) return false;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
      console.log('[Audio] ✅ AudioContext resumed');
      return true;
    } catch (err) {
      console.error('[Audio] ❌ Errore resume AudioContext:', err);
      return false;
    }
  }
  return true;
}

interface ToneConfig {
  frequency: number;    // Hz
  duration: number;     // ms
  type?: OscillatorType;
  volume?: number;      // 0-1
}

/**
 * Riproduce un singolo tono
 */
async function playTone(config: ToneConfig): Promise<void> {
  const ctx = initAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    await ctx.resume();
  }

  const { frequency, duration, type = 'sine', volume = 0.4 } = config;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.frequency.value = frequency;
  oscillator.type = type;

  // Attack-decay envelope per suono più naturale
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration / 1000);

  return new Promise(resolve => setTimeout(resolve, duration));
}

/**
 * Sequenza di toni per notifica standard (chat message)
 * Pattern: DO-MI-SOL (accordo maggiore ascendente)
 */
export async function playNotificationSequence(): Promise<void> {
  console.log('[Audio] 🔔 Riproduzione sequenza notifica');

  // Accordo C maggiore ascendente: C5-E5-G5
  const tones: ToneConfig[] = [
    { frequency: 523, duration: 100, volume: 0.4 },   // C5 (Do)
    { frequency: 659, duration: 100, volume: 0.4 },   // E5 (Mi)
    { frequency: 784, duration: 150, volume: 0.5 },   // G5 (Sol)
  ];

  for (const tone of tones) {
    await playTone(tone);
    await new Promise(r => setTimeout(r, 30)); // Piccola pausa tra i toni
  }
}

/**
 * Sequenza urgente per richieste foto - più lunga e variata
 * Pattern: Sirena bassa-alta ripetuta con finale acuto
 */
export async function playUrgentNotification(): Promise<void> {
  console.log('[Audio] 🚨 Riproduzione notifica urgente');

  // Pattern sirena + finale distintivo
  const sequence = [
    // Prima coppia sirena
    { frequency: 600, duration: 120, volume: 0.5, type: 'square' as OscillatorType },
    { frequency: 900, duration: 120, volume: 0.5, type: 'square' as OscillatorType },
    // Seconda coppia sirena
    { frequency: 600, duration: 120, volume: 0.5, type: 'square' as OscillatorType },
    { frequency: 900, duration: 120, volume: 0.5, type: 'square' as OscillatorType },
    // Finale distintivo ascendente
    { frequency: 1000, duration: 80, volume: 0.6, type: 'sine' as OscillatorType },
    { frequency: 1200, duration: 80, volume: 0.6, type: 'sine' as OscillatorType },
    { frequency: 1400, duration: 150, volume: 0.7, type: 'sine' as OscillatorType },
  ];

  for (const tone of sequence) {
    await playTone(tone);
  }
}

/**
 * Sequenza per messaggio chat - 6 note distintive per ambiente rumoroso
 * Pattern: Melodia ascendente-discendente tipo "notifica importante"
 * Volume aumentato per udibilità in officina
 */
export async function playChatNotification(): Promise<void> {
  console.log('[Audio] 💬 Riproduzione notifica chat (6 note)');

  // Sequenza melodica distintiva: su-su-giù-su-su-finale
  const tones: ToneConfig[] = [
    { frequency: 800, duration: 100, volume: 0.6 },    // Nota 1: G5
    { frequency: 1000, duration: 100, volume: 0.65 },  // Nota 2: B5
    { frequency: 900, duration: 100, volume: 0.6 },    // Nota 3: A5 (giù)
    { frequency: 1100, duration: 100, volume: 0.65 },  // Nota 4: C#6 (su)
    { frequency: 1300, duration: 100, volume: 0.7 },   // Nota 5: E6 (su)
    { frequency: 1500, duration: 150, volume: 0.75 },  // Nota 6: G6 finale (acuta)
  ];

  for (const tone of tones) {
    await playTone(tone);
    await new Promise(r => setTimeout(r, 40)); // Pausa breve tra note
  }
}

/**
 * Sequenza per richiesta foto in arrivo - molto distintiva
 * Pattern: Triade discendente + ascendente (richiama attenzione)
 */
export async function playPhotoRequestNotification(): Promise<void> {
  console.log('[Audio] 📸 Riproduzione notifica richiesta foto');

  // Pattern distintivo: giù-su-giù-su con frequenze diverse
  const sequence = [
    // Prima frase: discendente
    { frequency: 1200, duration: 100, volume: 0.5 },
    { frequency: 900, duration: 100, volume: 0.45 },
    { frequency: 700, duration: 100, volume: 0.4 },
    // Pausa
    // Seconda frase: ascendente più acuta
    { frequency: 800, duration: 100, volume: 0.45 },
    { frequency: 1000, duration: 100, volume: 0.5 },
    { frequency: 1400, duration: 150, volume: 0.6 },
    // Finale
    { frequency: 1600, duration: 200, volume: 0.55 },
  ];

  let i = 0;
  for (const tone of sequence) {
    await playTone(tone);
    i++;
    if (i === 3) {
      await new Promise(r => setTimeout(r, 80)); // Pausa tra le frasi
    }
  }
}

/**
 * Notifica magazzino - trasferimento/richiesta in arrivo
 * Pattern: 3 toni brevi distintivi (tipo "ping" magazzino)
 */
export async function playMagazzinoNotification(): Promise<void> {
  console.log('[Audio] 📦 Riproduzione notifica magazzino');

  // Pattern distintivo: 3 note veloci tipo "ding-ding-ding"
  const tones: ToneConfig[] = [
    { frequency: 880, duration: 80, volume: 0.5 },   // A5
    { frequency: 1047, duration: 80, volume: 0.55 }, // C6
    { frequency: 1319, duration: 120, volume: 0.6 }, // E6 (finale più lungo)
  ];

  for (const tone of tones) {
    await playTone(tone);
    await new Promise(r => setTimeout(r, 50));
  }
}

/**
 * Beep di conferma (azione completata)
 * Pattern: Due toni brevi ascendenti
 */
export async function playConfirmationBeep(): Promise<void> {
  console.log('[Audio] ✅ Riproduzione beep conferma');

  await playTone({ frequency: 800, duration: 80, volume: 0.3 });
  await new Promise(r => setTimeout(r, 30));
  await playTone({ frequency: 1200, duration: 100, volume: 0.35 });
}

/**
 * Beep di errore/rifiuto
 * Pattern: Due toni discendenti
 */
export async function playErrorBeep(): Promise<void> {
  console.log('[Audio] ❌ Riproduzione beep errore');

  await playTone({ frequency: 400, duration: 150, volume: 0.4, type: 'square' });
  await new Promise(r => setTimeout(r, 50));
  await playTone({ frequency: 300, duration: 200, volume: 0.35, type: 'square' });
}

/**
 * Notifica con vibrazione (per mobile)
 */
export function vibrateDevice(pattern: number[] = [100, 50, 100]): void {
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(pattern);
    } catch (err) {
      console.log('[Audio] Vibrazione non disponibile');
    }
  }
}

/**
 * Notifica completa: audio + vibrazione
 * Usa questa per notifiche importanti in ambiente rumoroso
 */
export async function playFullNotification(type: 'chat' | 'photo_request' | 'urgent' | 'confirm' | 'error' | 'magazzino' = 'chat'): Promise<void> {
  // Vibrazione per feedback tattile
  switch (type) {
    case 'urgent':
    case 'photo_request':
      vibrateDevice([100, 50, 100, 50, 200]); // Vibrazione più lunga
      break;
    case 'error':
      vibrateDevice([200, 100, 200]); // Doppia vibrazione
      break;
    case 'magazzino':
      vibrateDevice([100, 50, 100]); // Standard
      break;
    default:
      vibrateDevice([100, 50, 100]); // Standard
  }

  // Audio
  switch (type) {
    case 'chat':
      await playChatNotification();
      break;
    case 'photo_request':
      await playPhotoRequestNotification();
      break;
    case 'urgent':
      await playUrgentNotification();
      break;
    case 'confirm':
      await playConfirmationBeep();
      break;
    case 'error':
      await playErrorBeep();
      break;
    case 'magazzino':
      await playMagazzinoNotification();
      break;
  }
}

/**
 * Test tutte le notifiche (per debug)
 */
export async function testAllNotifications(): Promise<void> {
  console.log('[Audio] 🧪 Test di tutte le notifiche...');

  console.log('1. Chat notification');
  await playChatNotification();
  await new Promise(r => setTimeout(r, 500));

  console.log('2. Photo request notification');
  await playPhotoRequestNotification();
  await new Promise(r => setTimeout(r, 500));

  console.log('3. Urgent notification');
  await playUrgentNotification();
  await new Promise(r => setTimeout(r, 500));

  console.log('4. Confirmation beep');
  await playConfirmationBeep();
  await new Promise(r => setTimeout(r, 500));

  console.log('5. Error beep');
  await playErrorBeep();

  console.log('[Audio] ✅ Test completato');
}
